import axios, { AxiosError } from 'axios';
import { Cookies } from 'react-cookie';
import env from 'react-dotenv';
import i18n from '../i18n';
import { expireSessionAndRedirect } from './sessionExpiry';
import { initSessionRefresh, refreshSession } from './sessionRefresh';

const API_BASE_URL = env.REACT_APP_API_BASE_URL;
const cookies = new Cookies();
const DEFAULT_API_TIMEOUT_MS = 30000;

export const getApiTimeoutMs = (): number => {
  const configured = Number(env.REACT_APP_API_TIMEOUT_MS);
  return Number.isFinite(configured) && configured > 0
    ? configured
    : DEFAULT_API_TIMEOUT_MS;
};

/**
 * Gets a user-friendly error message based on HTTP status code
 * @param status - HTTP status code
 * @returns User-friendly error message
 */
const getStatusErrorMessage = (status: number): string => {
  switch (status) {
    case 400:
      return i18n.t('error.invalid_request');
    case 401:
      return i18n.t('error.session_expired');
    case 403:
      return i18n.t('error.permission_denied');
    case 404:
      return i18n.t('error.resource_not_found');
    case 408:
      return i18n.t('error.request_timeout');
    case 409:
      return i18n.t('error.conflict');
    case 422:
      return i18n.t('error.validation_error');
    case 429:
      return i18n.t('error.too_many_requests');
    case 500:
      return i18n.t('error.server_error');
    case 502:
      return i18n.t('error.bad_gateway');
    case 503:
      return i18n.t('error.service_unavailable');
    case 504:
      return i18n.t('error.gateway_timeout');
    default:
      if (status >= 400 && status < 500) {
        return i18n.t('error.client_error');
      } else if (status >= 500) {
        return i18n.t('error.server_error_generic');
      }
      return i18n.t('error.unexpected_error');
  }
};

/**
 * Extracts error message from axios error response
 * @param error - Axios error
 * @returns Error message string
 */
const extractErrorMessage = (error: AxiosError): string => {
  // Try to get message from response data
  if (error.response?.data) {
    const data = error.response.data as { message?: string; error?: string };
    if (data.message) {
      return data.message;
    }
    if (data.error) {
      return data.error;
    }
  }

  // If no message in response, use status code message
  if (error.response?.status) {
    return getStatusErrorMessage(error.response.status);
  }

  // Handle network errors (no response)
  if (error.request && !error.response) {
    if (error.code === 'ECONNABORTED') {
      return i18n.t('error.connection_timeout');
    }
    if (error.message === 'Network Error') {
      return i18n.t('error.network_error');
    }
    return i18n.t('error.connection_failed');
  }

  // Fallback to error message or default
  return error.message || i18n.t('error.unexpected_error');
};

const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: getApiTimeoutMs(),
});

/** End the session with the right message: the backend classifies failures
 *  (TOKEN_REVOKED = signed out on another device; anything else = expired). */
const finishLogout = (error: AxiosError): void => {
  console.warn('[Axios] 401 Unauthorized - logging out user');
  const rawMessage = JSON.stringify(error.response?.data ?? '');
  expireSessionAndRedirect(
    rawMessage.includes('TOKEN_REVOKED') ? 'revoked' : 'expired'
  );
};

const toFriendlyError = (
  error: AxiosError
): Error & { status?: number; responseData?: unknown } => {
  const friendlyError = new Error(extractErrorMessage(error)) as Error & {
    status?: number;
    responseData?: unknown;
  };
  friendlyError.status = error.response?.status;
  friendlyError.responseData = error.response?.data;
  return friendlyError;
};

// Arm the proactive refresh timer for tabs that load already logged in.
initSessionRefresh();

axiosInstance.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    // Don't logout on OPTIONS requests (CORS preflight) - these don't have auth headers
    const isOptionsRequest = error.config?.method?.toUpperCase() === 'OPTIONS';

    // Log for debugging
    if (error.response) {
      console.error('[Axios] Request failed:', {
        url: error.config?.url,
        method: error.config?.method,
        status: error.response.status,
        isOptions: isOptionsRequest,
        message: extractErrorMessage(error),
      });
    } else if (error.request) {
      console.error('[Axios] Network error:', {
        url: error.config?.url,
        method: error.config?.method,
        message: error.message,
        code: error.code,
      });
    }

    // Handle 401 Unauthorized: try one silent refresh + retry, then logout.
    const requestConfig = error.config as (typeof error.config & {
      _authRetried?: boolean;
    }) | undefined;
    const isAuthRoute = (requestConfig?.url ?? '').includes('/auth/');
    if (
      error.response &&
      error.response.status === 401 &&
      !isOptionsRequest &&
      !isAuthRoute
    ) {
      if (requestConfig && !requestConfig._authRetried) {
        return refreshSession().then((newToken) => {
          if (newToken) {
            requestConfig._authRetried = true;
            requestConfig.headers = requestConfig.headers ?? {};
            requestConfig.headers.Authorization = `Bearer ${newToken}`;
            return axiosInstance(requestConfig);
          }
          finishLogout(error);
          return Promise.reject(toFriendlyError(error));
        });
      }
      finishLogout(error);
    }

    // Preserve the HTTP status (e.g. 404 = not linked) and the response body
    // (binary download callers decode JSON error envelopes from Blobs) —
    // never the request config, which carries credentials.
    return Promise.reject(toFriendlyError(error));
  }
);

axiosInstance.interceptors.request.use((config) => {
  const token = cookies.get('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export default axiosInstance;
